/**
 * CommunitySuccessModal Component
 *
 * Displays a success modal after community creation.
 * Shows confirmation message with action buttons and social share options.
 */

import { toast } from "sonner"
import {
  FacebookIcon,
  InstagramIcon,
  LinkShareIcon,
  ThreadsIcon,
  TikTokIcon,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader } from "@/components/ui/dialog"
import { useTranslation } from "@/language"

interface CommunitySuccessModalProps {
  isOpen: boolean
  communityId: string
  communityName?: string
  overlayClass?: string
  onViewCommunity: () => void
}

export function CommunitySuccessModal({
  isOpen,
  communityId,
  overlayClass,
  onViewCommunity,
}: CommunitySuccessModalProps) {
  const { t } = useTranslation("communities")

  const communityUrl = `${window.location.origin}/communities/${communityId}`

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(communityUrl)
      toast.success(t("createCommunity.linkCopied"))
    } catch {
      toast.error(t("createCommunity.linkCopyFailed"))
    }
  }

  const handleInviteFriends = () => {
    onViewCommunity()
  }

  return (
    <Dialog open={isOpen}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-[600px] text-center p-8"
        overlayClass={overlayClass}
      >
        <DialogHeader className="flex flex-col items-center">
          {/* Title */}
          <h2 className="text-[24px] font-bold text-white mt-6">
            {t("createCommunity.communityIsLive")}
          </h2>

          {/* Description */}
          <DialogDescription className="text-primary mt-3 text-center">
            <p>{t("createCommunity.exploreDescription")}</p>
          </DialogDescription>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 mt-1 items-center justify-center w-full">
          {/* Primary Button - View */}
          <Button onClick={onViewCommunity} className="w-[280px] bg-white font-semibold uppercase">
            {t("createCommunity.view")}
          </Button>

          {/* Secondary Button - Invite Friends */}
          <Button
            onClick={handleInviteFriends}
            variant="outline"
            className="w-full border-white text-white w-[280px] font-semibold uppercase"
          >
            {t("createCommunity.inviteFriends")}
          </Button>
        </div>

        {/* Share to Social Media Section */}
        <div className="mt-3 mb-7">
          <p className="text-white text-sm mb-3">{t("createCommunity.shareToSocial")}</p>

          {/* Social Media Icons */}
          <div className="flex items-center justify-center gap-2.5 mt-1">
            {/* Instagram */}
            <button
              type="button"
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30 flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Instagram"
            >
              <InstagramIcon width={20} height={20} className="text-white" strokeWidth={2} />
            </button>

            {/* Facebook */}
            <button
              type="button"
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Facebook"
            >
              <FacebookIcon width={18} height={18} className="text-off-white" />
            </button>

            {/* TikTok */}
            <button
              type="button"
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="TikTok"
            >
              <TikTokIcon width={14} height={16} className="text-white" />
            </button>

            {/* Threads */}
            <button
              type="button"
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Threads"
            >
              <ThreadsIcon width={15} height={18} className="text-off-white" />
            </button>

            {/* Link Button */}
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Copy link"
            >
              <LinkShareIcon width={15} height={8} className="text-off-white" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
